import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { connect } from 'redux-bundler-react';
import LinkIcon from '@mui/icons-material/Link';
import React from 'react';

import ConfirmationDialog from './ConfirmationDialog';

const styles = theme => ({
  list: {
    maxHeight: 450,
    overflow: 'auto',
  },
  listItem: {
    cursor: 'pointer',
  },
  inactiveItem: {
    opacity: 0.5,
  },
  spacer: {
    flexGrow: 1,
  },
  currentBox: {
    padding: theme.spacing(2),
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[50],
  },
  currentRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
  },
  branchChip: {
    display: 'inline-block',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '0.9rem',
    padding: theme.spacing(0.25, 1),
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(110, 118, 129, 0.2)'
        : 'rgba(175, 184, 193, 0.15)',
  },
});

const SplitTestDialog = ({
  classes,
  open,
  onClose,
  doAddSplitTest,
  doFetchSplitTests,
  activeSplitTests,
  inactiveSplitTests,
  splitTestLoading,
}) => {
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [clipboardSnackbarOpen, setClipboardSnackbarOpen] = React.useState(
    false,
  );

  React.useEffect(() => {
    if (open) {
      doFetchSplitTests();
    }
  }, [open, doFetchSplitTests]);

  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('nf_ab='));
  const currentBranch = cookie ? cookie.split('=')[1] : null;

  const switchableBranches = activeSplitTests.filter(
    st => st.name !== currentBranch,
  );

  const totalBranches = activeSplitTests.length + inactiveSplitTests.length;

  const disableSplitTest = () => {
    document.cookie =
      'nf_ab= ; expires = Thu, 01 Jan 1970 00:00:00 GMT; path=/';

    setShowConfirmation(true);
    setMessage('Split test disabled. Reloading to main branch.');
    onClose();
  };

  const selectBranch = name => {
    var now = new Date();
    var expires = now.getTime() + 1000 * 3600 * 24 * 365;
    now.setTime(expires);

    document.cookie = `nf_ab=${name}; expires=${now.toUTCString()}; path=/`;
    doAddSplitTest(name);

    setShowConfirmation(true);
    setMessage('Please reload the page for the change to take effect');
    onClose();
  };

  const copyClipboardLink = name => {
    window.navigator.clipboard.writeText(`https://app.archpt.io/st/${name}`);
    setClipboardSnackbarOpen(true);
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    window.location.reload();
  };

  return (
    <>
      <ConfirmationDialog
        title="Split Test Configured"
        open={showConfirmation}
        onClose={handleCloseConfirmation}
        message={message}
      />
      <Dialog maxWidth="xs" fullWidth open={open} onClose={onClose}>
        <Snackbar
          open={clipboardSnackbarOpen}
          autoHideDuration={6000}
          onClose={() => setClipboardSnackbarOpen(false)}
          message="Link copied to your clipboard"
        />

        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="h6">Split test</Typography>
          <Typography variant="body2" color="textSecondary">
            {totalBranches} branch{totalBranches !== 1 ? 'es' : ''} available
          </Typography>
        </Box>

        <Divider />

        {currentBranch && (
          <div className={classes.currentBox}>
            <Chip label="CURRENT" size="small" color="primary" variant="outlined" sx={{ mb: 1 }} />
            <div className={classes.currentRow}>
              <span className={classes.branchChip}>{currentBranch}</span>
              <Tooltip title="Copy split test link">
                <IconButton
                  size="small"
                  onClick={() => copyClipboardLink(currentBranch)}
                >
                  <LinkIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        )}

        {switchableBranches.length > 0 && (
          <List
            className={classes.list}
            subheader={<ListSubheader>Switch to</ListSubheader>}
          >
            {switchableBranches.map(st => (
              <ListItem
                key={st.id}
                className={classes.listItem}
                onClick={() => selectBranch(st.name)}
                divider
                button
              >
                <ListItemText
                  primary={
                    <span className={classes.branchChip}>{st.name}</span>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Copy split test link">
                    <IconButton
                      edge="end"
                      onClick={() => copyClipboardLink(st.name)}
                    >
                      <LinkIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        {inactiveSplitTests.length > 0 && (
          <List
            className={classes.list}
            subheader={<ListSubheader>Inactive</ListSubheader>}
          >
            {inactiveSplitTests.map(st => (
              <ListItem key={st.id} className={classes.inactiveItem} divider>
                <ListItemText
                  primary={
                    <span className={classes.branchChip}>{st.name}</span>
                  }
                />
                <Chip label="INACTIVE" size="small" variant="outlined" />
              </ListItem>
            ))}
          </List>
        )}

        <DialogActions>
          <Button variant="outlined" onClick={disableSplitTest}>
            Disable Split Test
          </Button>
          <div className={classes.spacer} />
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default connect(
  'doAddSplitTest',
  'doFetchSplitTests',
  'selectActiveSplitTests',
  'selectInactiveSplitTests',
  'selectSplitTestLoading',
  withStyles(SplitTestDialog, styles, { withTheme: true }),
);
